/**
 * ModalHeader — project modal header with stats + delete action
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Project } from '../../../api/types'
import { deleteProject, archiveProject, fetchChangeOrderLog, projectSourceDocumentUrl } from '../../../api/projects'
import { apiFetch } from '../../../api/client'
import { formatCurrency } from '../../../lib/formatters'
import {
  formatOutstandingForProject,
  formatRetainageForProject,
  billingProgressForProject,
} from '../../../lib/projectMoneyDisplay'
import PulseHubTile from '../../../features/pulse/PulseHubTile'
import { PulseAddFilesButton } from '../../../features/pulse/PulseAddFiles.tsx'
import PayAppCta from '../../../components/billing/PayAppCta'
import ArOpenButton from '../../../components/billing/ArOpenButton'
import type { PayApp } from '../../../api/types'
import { fetchPulseTimeline } from '../../../features/pulse/pulseApi'
import '../../../features/pulse/pulse.css'
import { markHubPanelReady } from '../../../lib/hubPanelPerf'

function statusChip(p: Project): { label: string; cls: string } {
  if (p.completed_at) {
    const m = String(p.completed_at).match(/^(\d{4})-(\d{2})-(\d{2})/)
    const label = m
      ? `Completed ${new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : 'Completed'
    return { label, cls: 'c-gry' }
  }
  if (p.status === 'completed') return { label: 'Completed', cls: 'c-gry' }
  const out = parseFloat(p.outstanding ?? '0')
  if (out > 0) return { label: 'Balance due', cls: 'c-amb' }
  return { label: 'Paid up', cls: 'c-grn' }
}

interface Props {
  project:     Project
  payApps?:    PayApp[]
  draftTotal?: number
  onClose:     () => void
  onDeleted?:  () => void
  onOpenAr?:   () => void
}

export default function ModalHeader({ project, payApps = [], draftTotal = 0, onClose, onDeleted, onOpenAr }: Props) {
  useEffect(() => {
    markHubPanelReady()
  }, [project.id])

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const qc = useQueryClient()
  const { label, cls } = statusChip(project)
  const outstanding = parseFloat(project.outstanding ?? '0')
  const received = parseFloat(project.payments_received ?? '0')
  const coTotal = parseFloat(project.change_order_total ?? '0') || 0
  const originalContract = parseFloat(project.original_contract ?? '0') || 0
  const revisedContract = originalContract + coTotal
  const progress = billingProgressForProject(project)
  const showDraftLine = draftTotal > 0
  const [hl, setHl] = useState<string | null>(null)

  useEffect(() => {
    const raw = searchParams.get('hl')
    if (!raw) return
    const key = raw.toLowerCase()
    if (key !== 'outstanding' && key !== 'received' && key !== 'retainage') return
    setHl(key)
    const next = new URLSearchParams(searchParams)
    next.delete('hl')
    setSearchParams(next, { replace: true })
    const t = window.setTimeout(() => setHl(null), 2000)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id])

  const { data: sovUploads = [] } = useQuery({
    queryKey: ['sov-uploads', project.id],
    queryFn: () => apiFetch<Array<{ original_name?: string }>>('GET', `/api/projects/${project.id}/sov/uploads`),
    enabled: !!project.id,
    staleTime: 60_000,
  })
  const hasSourceDoc = sovUploads.some((u: { original_name?: string }) => u.original_name?.startsWith('Imported proposal —'))
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const [deleteErr, setDeleteErr] = useState<string | null>(null)
  const [showArchiveOption, setShowArchiveOption] = useState(false)
  const [archiveBusy, setArchiveBusy] = useState(false)
  const { data: closeoutTl } = useQuery({
    queryKey: ['pulse-closeout-exists', project.id],
    queryFn: () => fetchPulseTimeline(project.id, { types: 'closeout_generated' }),
    staleTime: 60_000,
  })
  const { data: coLog } = useQuery({
    queryKey: ['change-order-log', project.id],
    queryFn: () => fetchChangeOrderLog(project.id),
    staleTime: 60_000,
  })
  const coCount = coLog?.entries?.length ?? 0
  const hideCloseJob = (closeoutTl?.events || []).some((e) => e.type === 'closeout_generated')

  function openCoLogOnPulse() {
    navigate(`/projects/${project.id}/pulse#co-log-tree`)
  }

  const deleteMutation = useMutation({
    mutationFn: () => deleteProject(project.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['projects'] })
      setConfirmOpen(false)
      setShowArchiveOption(false)
      onDeleted?.()
      onClose()
    },
    onError: (err: Error) => {
      const msg = err.message || 'Delete failed'
      setDeleteErr(msg)
      if (/paid pay applications/i.test(msg)) setShowArchiveOption(true)
    },
  })

  async function handleArchive() {
    setArchiveBusy(true)
    setDeleteErr(null)
    try {
      await archiveProject(project.id)
      await qc.invalidateQueries({ queryKey: ['projects'] })
      setConfirmOpen(false)
      setShowArchiveOption(false)
      onDeleted?.()
      onClose()
    } catch (err) {
      setDeleteErr(err instanceof Error ? err.message : 'Archive failed')
    } finally {
      setArchiveBusy(false)
    }
  }

  const canConfirmDelete = confirmName.trim() === (project.name ?? '').trim()

  return (
    <div className="pm-hdr" data-testid="hub-panel-ready">
      <div className="pm-hdr-top">
        <div className="pm-hdr-title-block">
          <h2 className="pm-hdr-title">{project.name ?? '—'}</h2>
          <span className={`chip ${cls}`}>{label}</span>
        </div>
        <div className="pm-hdr-top-actions">
          <button
            type="button"
            className="btn b-red btn-sm"
            data-testid="project-delete-btn"
            onClick={() => { setConfirmOpen(true); setConfirmName(''); setDeleteErr(null); setShowArchiveOption(false) }}
          >
            Delete project
          </button>
          <button className="pm-x" onClick={onClose} aria-label="Close modal">✕</button>
        </div>
      </div>
      <div className="pm-hdr-toolbar" data-testid="pm-hdr-toolbar">
        <PulseHubTile project={project} />
        {onOpenAr ? (
          <ArOpenButton project={project} testId="hub-ar-open-btn" onClick={onOpenAr} />
        ) : null}
        <PayAppCta
          projectId={project.id}
          project={project}
          payApps={payApps}
          testId="hub-new-payapp-btn"
        />
        {!hideCloseJob && (
          <button
            type="button"
            className="btn b-ol btn-sm"
            data-testid="hub-close-job"
            onClick={() => navigate(`/projects/${project.id}/pulse?tab=closeout`)}
          >
            Close job
          </button>
        )}
        <PulseAddFilesButton
          projectId={project.id}
          onUploaded={() => {
            void qc.invalidateQueries({ queryKey: ['project-inbox', project.id] })
            void qc.invalidateQueries({ queryKey: ['pulse-timeline', project.id] })
          }}
        />
      </div>

      <div className="pm-meta">
        {project.contact && <span>{project.contact}</span>}
        {project.owner && (
          <span>
            <b>Owner:</b>{' '}
            <button
              type="button"
              className="owner-statement-link"
              data-testid="pm-owner-statement-link"
              onClick={() => navigate(`/statements/owner?owner=${encodeURIComponent(project.owner!)}`)}
            >
              {project.owner}
            </button>
          </span>
        )}
        {project.contractor && <span><b>Contractor:</b> {project.contractor}</span>}
        {project.number  && <span><b>Proj #:</b> {project.number}</span>}
        {hasSourceDoc && (
          <a
            href={projectSourceDocumentUrl(project.id)}
            data-testid="pm-view-original-proposal"
            style={{ fontWeight: 600, color: 'var(--ink)' }}
          >
            View original
          </a>
        )}
      </div>

      <div className="pm-stats">
        <div className="pm-stat">
          <div className="pm-stat-l">{coTotal > 0 ? 'Revised contract' : 'Contract'}</div>
          <div className="pm-stat-v" data-testid="pm-contract-breakdown">
            {formatCurrency(coTotal > 0 ? revisedContract : project.original_contract)}
          </div>
          {coTotal > 0 && (
            <div className="pm-stat-cap" data-testid="pm-contract-caption">
              {formatCurrency(originalContract)} original + {formatCurrency(coTotal)} ({coCount} CO{coCount === 1 ? '' : 's'}) ·{' '}
              <button type="button" className="pm-stat-link" onClick={openCoLogOnPulse}>View</button>
            </div>
          )}
        </div>
        <div
          className={`pm-stat${hl === 'outstanding' ? ' pm-stat--hl' : ''}`}
          data-testid="pm-stat-outstanding"
          data-hl={hl === 'outstanding' ? 'outstanding' : undefined}
        >
          <div className="pm-stat-l">Outstanding</div>
          <div className={`pm-stat-v${outstanding > 0 ? ' red' : ''}`} style={outstanding === 0 && showDraftLine ? { color: 'var(--ink4)' } : undefined}>
            {formatOutstandingForProject(project, draftTotal)}
          </div>
          {outstanding > 0 && showDraftLine && (
            <div className="pm-stat-cap">+ {formatCurrency(draftTotal)} in drafts</div>
          )}
        </div>
        <div
          className={`pm-stat${hl === 'received' ? ' pm-stat--hl' : ''}`}
          data-testid="pm-stat-received"
          data-hl={hl === 'received' ? 'received' : undefined}
        >
          <div className="pm-stat-l">Received</div>
          <div className="pm-stat-v" style={{ color: received > 0 ? 'var(--green)' : undefined }}>
            {formatCurrency(received)}
          </div>
        </div>
        <div
          className={`pm-stat${hl === 'retainage' ? ' pm-stat--hl' : ''}`}
          data-testid="pm-stat-retainage"
          data-hl={hl === 'retainage' ? 'retainage' : undefined}
        >
          <div className="pm-stat-l">Retainage</div>
          <div className="pm-stat-v">{formatRetainageForProject(project)}</div>
        </div>
        <div className="pm-stat">
          <div className="pm-stat-l">Left to bill</div>
          <div className="pm-stat-v" data-testid="pm-left-to-bill">
            {progress.contractTotal > 0 ? formatCurrency(progress.remaining) : '—'}
          </div>
          {progress.contractTotal > 0 && (
            <div className="pm-stat-cap">{progress.pctBilled}% of revised contract billed</div>
          )}
        </div>
        <div className="pm-stat">
          <div className="pm-stat-l">Pay apps</div>
          <div className="pm-stat-v">{project.pay_app_count}</div>
        </div>
      </div>

      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="project-delete-confirm"
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 'var(--rs)',
            border: '1px solid var(--red)',
            background: 'var(--red-l)',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', marginBottom: 6 }}>
            Delete this project?
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink2)', marginBottom: 10, lineHeight: 1.5 }}>
            This hides the project from your hub and dashboards. Paid pay apps block deletion.
            Type <b>{project.name}</b> to confirm.
          </div>
          <input
            className="fld-i"
            value={confirmName}
            onChange={e => setConfirmName(e.target.value)}
            placeholder={project.name ?? 'Project name'}
            data-testid="project-delete-confirm-input"
            style={{ width: '100%', marginBottom: 10 }}
          />
          {deleteErr && <div style={{ color: 'var(--red)', fontSize: 11, marginBottom: 8 }}>{deleteErr}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn b-ol btn-sm" onClick={() => setConfirmOpen(false)}>Cancel</button>
            {showArchiveOption && (
              <button
                type="button"
                className="btn b-dk btn-sm"
                disabled={archiveBusy}
                data-testid="project-archive-instead-btn"
                onClick={() => void handleArchive()}
              >
                {archiveBusy ? 'Archiving…' : 'Archive instead'}
              </button>
            )}
            <button
              type="button"
              className="btn b-red btn-sm"
              disabled={!canConfirmDelete || deleteMutation.isPending || showArchiveOption}
              data-testid="project-delete-confirm-btn"
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
